import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    ASC, ADD, EDIT, IDLE, PENDING, YES,
} from "const"
import { Lifecycle } from "server/models"
import { StateType, hasProjectAccess } from "client/utils"
import { sort } from "client/store/utils"
import {
    PRM_CC_REQUIREMENT,
    StoreType,
    CcRequirementProps,
    RequirementLifecyclePhaseList,

    LoadCcRequirementDictionaries,
    LoadCcRequirementById,
    LoadAllLifecycles,
    LoadAllLifecyclePhases,

    setCcRequirementDictionariesLoaded,
    setCcRequirementEdit,
    setCcRequirementLifecyclesEdit,
    setCcRequirementLifecyclePhaseListEdit,
    setCcRequirementReloadEdit,

    initCcRequirement,
    moduleIds,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
} from "client/components"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"

const Edit = ({
    requirementId = null,
}: CcRequirementProps) => {
    const dictionariesLoaded = useSelector((state: StoreType) => state.ccRequirement.dictionariesLoaded)
    const requirement = useSelector((state: StoreType) => state.ccRequirement.requirementEdit)
    const reloadEdit = useSelector((state: StoreType) => state.ccRequirement.reloadEdit)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [dictionaryLoadState, setDictionaryLoadState] = useState<StateType>(IDLE)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const reload = () => {
        dispatch(setCcRequirementReloadEdit(false))
        setTimeout(() => {
            dispatch(setCcRequirementReloadEdit(true))
        }, 0)
    }

    useEffect(() => {
        if (dictionariesLoaded) return
        setDictionaryLoadState(PENDING)

        LoadCcRequirementDictionaries(userData.accountId, project.id, dispatch).then(() => {
            setDictionaryLoadState(IDLE)
            dispatch(setCcRequirementDictionariesLoaded(true))
        })
    }, [dictionariesLoaded])

    useEffect(() => {
        setLoadState(PENDING)
        const promiseArray: Promise<any>[] = []

        const projectModules = JSON.parse(project.modules)
        if (packageSettings.lifecycleModuleAccess === YES && projectModules[moduleIds.lifecycle]) {
            promiseArray.push(
                LoadAllLifecycles(userData.accountId, project.id)
                .then(({ lifecycleList }) => {
                    const lifecycles: Lifecycle[] = Object.values(lifecycleList)
                    lifecycles.sort((a, b) => sort(a, b, "name", ASC))

                    const promiseNestedArray: Promise<any>[] = []
                    const phaseList: RequirementLifecyclePhaseList = {}

                    lifecycles.forEach((lifecycle) => {
                        promiseNestedArray.push(
                            LoadAllLifecyclePhases(lifecycle.id).then((phases) => {
                                phases.sort((a, b) => sort(a, b, "name", ASC))
                                phaseList[lifecycle.id] = phases
                            }),
                        )
                    })
                    return Promise.all(promiseNestedArray).then(() => {
                        dispatch(setCcRequirementLifecyclesEdit(lifecycles))
                        dispatch(setCcRequirementLifecyclePhaseListEdit(phaseList))
                    })
                }),
            )
        }

        if (requirementId === 0) {
            Promise.all(promiseArray).then(() => {
                setLoadState(IDLE)
                dispatch(setCcRequirementEdit(initCcRequirement))
                reload()
            })
        } else {
            promiseArray.push(
                LoadCcRequirementById(requirementId, project.id).then((res) => {
                    dispatch(setCcRequirementEdit(res))
                }),
            )
            Promise.all(promiseArray).then(() => {
                setLoadState(IDLE)
                reload()
            })
        }
    }, [])

    const addAccess = hasProjectAccess(PRM_CC_REQUIREMENT, ADD)
    const editAccess = hasProjectAccess(PRM_CC_REQUIREMENT, EDIT)

    const allowedToEdit = editAccess || requirement.creator === userData.userId

    if (requirementId === 0 && !addAccess) return <AccessDenied />
    if (requirementId !== 0 && !allowedToEdit) return <AccessDenied />
    if (!requirement) return <NotFound />

    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    {requirement.id ? `${lang.editRequirement} # ${requirement.id}` : lang.addRequirement}
                </PageTitle>
            </PageHeader>
            <PageContent>
                {
                    (
                        dictionaryLoadState === PENDING
                        || loadState === PENDING
                    ) && <Loader />
                }
                {
                    !reloadEdit
                    && <Form requirement={requirement} />
                }
                {
                    reloadEdit
                    && <Form requirement={requirement} />
                }
            </PageContent>
        </Container>
    )
}
export default Edit
